import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AlertComponent, ButtonDirective } from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { AuthService } from 'auth';
import { Logger } from 'shared';
import { ImpersonationService } from 'app';

@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  imports: [CommonModule, AlertComponent, ButtonDirective, IconDirective],
  template: `
    @if (isImpersonating()) {
      <c-alert color="danger" class="impersonation-banner mb-0 rounded-0">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <svg cIcon name="cilWarning" size="lg"></svg>
            <span class="fw-bold">
              IMPERSONATING: {{ impersonatedUserName() }}
            </span>
          </div>
          <button
            cButton
            color="light"
            size="sm"
            (click)="stopImpersonating()"
            [disabled]="isStoppingImpersonation()">
            <svg cIcon name="cilAccountLogout" size="sm"></svg>
            {{ isStoppingImpersonation() ? 'Stopping...' : 'Stop Impersonating' }}
          </button>
        </div>
      </c-alert>
    }
  `,
  styles: [`
    /* Below the sticky header (CoreUI 1029): the header's dropdowns (notifications,
       language, account) open over the banner instead of disappearing under it. */
    .impersonation-banner {
      position: relative;
      z-index: 1020;
      border-left: none;
      border-right: none;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImpersonationBannerComponent {
  protected logger = new Logger('ImpersonationBannerComponent');
  private authService = inject(AuthService);
  private impersonationService = inject(ImpersonationService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  protected isStoppingImpersonation = signal(false);

  // Use toSignal to convert observable to signal for reactive updates
  private impersonationSession = toSignal(this.impersonationService.getSession$(), {
    initialValue: { active: false } as const
  });

  protected isImpersonating = computed(() => {
    const session = this.impersonationSession();
    return session?.active || false;
  });

  protected impersonatedUserName = computed(() => {
    const session = this.impersonationSession();
    if (!session || !session.active) {
      return 'Unknown User';
    }
    // Mirrors User#display_name: full_name is empty on many accounts that do have
    // a first/last name (e.g. every funnel sign-up), and username is often unset too.
    const user = session.impersonatedUser;
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    return user?.full_name || name || user?.username || user?.email ||
           (session.impersonatedUserId ? `#${session.impersonatedUserId}` : 'Unknown User');
  });

  protected stopImpersonating(): void {
    this.isStoppingImpersonation.set(true);

    this.authService.stopImpersonating()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.isStoppingImpersonation.set(false);
            // Navigate to home after stopping impersonation
            this.router.navigate(['/']);
          },
          error: (error) => {
            this.logger.error('Failed to stop impersonating', error);
            this.isStoppingImpersonation.set(false);
            alert('Failed to stop impersonating. Please try again.');
          }
        });
  }
}
